// SPDX-License-Identifier: GPL-3.0-or-later
// Accessories of the pinball SigmaBot, built from three.js primitives.
// Own code. The upstream 3D bot takes these parts from a binary parts file (a GLB). The table has
// no such file, so each accessory is a few primitives that follow the loft of the body.
// Rules: no clock, no random. All sizes are body units: up is +y, the front is +z.

import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  SphereGeometry,
  TorusGeometry,
} from "three";
import type { BodyDef } from "./bodies.ts";
import { type Colors, GOLD, INK, STEEL } from "./kit.ts";
import { DEPTH_K, frontZ, halfAt } from "./loft.ts";
import { part } from "./parts.ts";
import type { SigmaBotAccessory, SigmaBotBody } from "./spec.ts";

/** What an accessory builder reads. */
interface AccCtx {
  readonly kind: SigmaBotBody;
  readonly b: BodyDef;
  readonly c: Colors;
}

type AccBuilder = (g: Group, a: AccCtx) => void;

/** The z in front of the eyes where glasses sit. */
function eyeZ({ kind, b }: AccCtx): number {
  return frontZ(kind, -b.faceY, b.eyeDx) + b.eyeR * 0.6;
}

function lenses(g: Group, a: AccCtx, radius: number, tube: number): void {
  const y = -a.b.faceY;
  const z = eyeZ(a);
  for (const s of [-1, 1]) {
    part(g, new TorusGeometry(radius, tube, 6, 16), INK, s * a.b.eyeDx, y, z);
  }
  part(g, new BoxGeometry(2 * a.b.eyeDx - 2 * radius, 0.8, 0.8), INK, 0, y, z);
}

/** A ring around the body at height `y`, as wide as the body there. */
function collar(g: Group, a: AccCtx, y: number, tube: number, color: Colors["accent"]): void {
  part(g, new TorusGeometry(halfAt(a.kind, y) + 1, tube, 8, 24), color, 0, y, 0, {
    rx: Math.PI / 2,
    sy: DEPTH_K[a.kind],
  });
}

const ACCESSORIES: Record<Exclude<SigmaBotAccessory, "none">, AccBuilder> = {
  glasses: (g, a) => lenses(g, a, a.b.eyeR * 1.15, 0.7),
  shades: (g, a) => {
    const y = -a.b.faceY;
    const z = eyeZ(a);
    for (const s of [-1, 1]) {
      part(
        g,
        new CylinderGeometry(a.b.eyeR * 1.2, a.b.eyeR * 1.2, 1.2, 14),
        INK,
        s * a.b.eyeDx,
        y,
        z,
        {
          rx: Math.PI / 2,
        },
      );
    }
    part(g, new BoxGeometry(2 * a.b.eyeDx, 0.9, 0.9), INK, 0, y, z);
  },
  monocle: (g, a) => {
    const r = a.b.eyeR * 1.3;
    const y = -a.b.faceY;
    part(g, new TorusGeometry(r, 0.7, 6, 16), GOLD, a.b.eyeDx, y, eyeZ(a));
    part(g, new BoxGeometry(0.5, 12, 0.5), GOLD, a.b.eyeDx + r * 0.9, y - 7, eyeZ(a));
  },
  mask: (g, a) => {
    const y = -a.b.faceY;
    const reach = a.b.eyeDx + a.b.eyeR * 1.3;
    const n = 7;
    for (let i = 0; i < n; i++) {
      const x = -reach + (2 * reach * i) / (n - 1);
      part(
        g,
        new BoxGeometry(((2 * reach) / (n - 1)) * 1.15, a.b.eyeR * 2.4, 1.2),
        a.c.accent,
        x,
        y,
        frontZ(a.kind, y, x) + 0.6,
      );
    }
  },
  scarf: (g, a) => {
    const y = -a.b.neckY;
    collar(g, a, y, 2.6, a.c.accent);
    const x = halfAt(a.kind, y) * 0.4;
    part(g, new BoxGeometry(5, 14, 2.4), a.c.accentDark, x, y - 8, frontZ(a.kind, y - 4, x) + 1.5, {
      rz: 0.1,
    });
  },
  bowtie: (g, a) => {
    const y = -a.b.neckY;
    const z = frontZ(a.kind, y, 0) + 2;
    for (const s of [-1, 1]) {
      part(g, new ConeGeometry(4.5, 8, 8), a.c.accent, s * 5, y, z, { rz: -s * (Math.PI / 2) });
    }
    part(g, new SphereGeometry(2, 8, 6), a.c.accentDark, 0, y, z);
  },
  antenna: (g, a) => {
    const top = -a.b.topY;
    part(g, new CylinderGeometry(0.7, 0.7, 14, 6), STEEL, 0, top + 5, 0);
    part(g, new SphereGeometry(2.4, 8, 6), a.c.accent, 0, top + 12.5, 0);
  },
  cape: (g, a) => {
    const top = -a.b.shoulderY + 4;
    const bottom = 6;
    const y = (top + bottom) / 2;
    const depth = DEPTH_K[a.kind] * halfAt(a.kind, y);
    part(
      g,
      new BoxGeometry(2 * halfAt(a.kind, -a.b.shoulderY), top - bottom, 1.4),
      a.c.accentDark,
      0,
      y,
      -depth - 1.2,
    );
  },
  backpack: (g, a) => {
    const y = -a.b.midY;
    const w = halfAt(a.kind, y) * 1.2;
    const depth = DEPTH_K[a.kind] * halfAt(a.kind, y);
    part(g, new BoxGeometry(w, w * 1.2, 7), a.c.accentDark, 0, y, -depth - 3);
  },
  necklace: (g, a) => {
    const y = -a.b.neckY;
    collar(g, a, y, 0.6, GOLD);
    part(g, new SphereGeometry(2.4, 8, 6), a.c.accent, 0, y - 5, frontZ(a.kind, y - 5, 0) + 1.5);
  },
  earrings: (g, a) => {
    const y = -a.b.faceY;
    for (const s of [-1, 1]) {
      part(
        g,
        new SphereGeometry(1.8, 8, 6),
        GOLD,
        s * (halfAt(a.kind, y) + 0.4),
        y - a.b.eyeR * 1.6,
        0,
      );
    }
  },
  headphones: (g, a) => {
    const y = -a.b.faceY + 2;
    const x = halfAt(a.kind, y) + 1.5;
    for (const s of [-1, 1]) {
      part(g, new CylinderGeometry(5, 5, 4, 12), a.c.accent, s * x, y, 0, { rz: Math.PI / 2 });
    }
    // The band is an arc over the head. It is as high as the body top.
    const rise = -a.b.topY - y + 1.5;
    part(g, new TorusGeometry(x, 0.9, 6, 16, Math.PI), a.c.accentDark, 0, y, 0, { sy: rise / x });
  },
  badge: (g, a) => {
    const x = -a.b.rx * 0.45;
    const y = -a.b.shoulderY + 4;
    part(g, new CylinderGeometry(3.4, 3.4, 1, 14), GOLD, x, y, frontZ(a.kind, y, x) + 0.5, {
      rx: Math.PI / 2,
    });
  },
};

/** Add the accessory to a group. `none` adds nothing. */
export function addAccessory(
  parent: Group,
  kind: SigmaBotBody,
  b: BodyDef,
  c: Colors,
  accessory: SigmaBotAccessory,
): void {
  if (accessory === "none") return;
  const g = new Group();
  g.name = "SigmaBotAccessory";
  ACCESSORIES[accessory](g, { kind, b, c });
  parent.add(g);
}
